import { LegalPageShell, LegalSection, LegalP, LegalList, LegalSubhead, T } from "./LegalShell";

export default function TermsPage() {
  return (
    <LegalPageShell
      badge="Legal"
      title="Terms of"
      titleEm="Service"
      lastUpdated="14 June 2026"
    >
      <LegalSection id="intro" num="00" title="Welcome to JiraniApp">
        <LegalP>
          These Terms of Service ("Terms") govern your access to and use of JiraniApp, including our
          website, mobile applications, and related services (together, the "Platform"). JiraniApp
          connects customers ("Clients") with independent home service professionals ("JiraniPros") for
          the booking and delivery of home services.
        </LegalP>
        <LegalP>
          By creating an account, browsing the Platform, or booking a service, you agree to be bound by
          these Terms. If you do not agree, please do not use JiraniApp.
        </LegalP>
      </LegalSection>

      <LegalSection id="eligibility" num="01" title="Eligibility and accounts">
        <LegalP>
          You must be at least 18 years old to create an account on JiraniApp. By registering, you
          confirm that the information you provide, including your name, phone number, and location, is
          accurate and kept up to date.
        </LegalP>
        <LegalList items={[
          "You are responsible for keeping your login credentials secure and confidential.",
          "You are responsible for all activity that occurs under your account.",
          "JiraniApp may suspend or terminate accounts that provide false information, violate these Terms, or are used for fraudulent or unsafe activity.",
        ]} />
        <LegalSubhead>JiraniPro accounts</LegalSubhead>
        <LegalP>
          JiraniPros must complete identity verification and any required background checks before being
          listed on the Platform. JiraniApp reserves the right to decline, suspend, or remove a
          professional from the Platform at its discretion, including where complaints, safety concerns,
          or policy violations arise.
        </LegalP>
      </LegalSection>

      <LegalSection id="services" num="02" title="Bookings and services">
        <LegalP>
          JiraniApp is a marketplace that facilitates connections between Clients and JiraniPros.
          JiraniPros are independent professionals or independent businesses, not employees, agents, or
          representatives of JiraniApp.
        </LegalP>
        <LegalList items={[
          "When you book a service, you enter into a direct arrangement with the JiraniPro for the performance of that service.",
          "Service descriptions, pricing, and availability are set by JiraniPros and may vary.",
          "JiraniApp does not guarantee the availability of any specific professional at any specific time, although we aim to provide accurate, real-time information.",
          "Clients are responsible for providing accurate booking details, including address, access instructions, and any special requirements.",
        ]} />
      </LegalSection>

      <LegalSection id="payments" num="03" title="Payments, fees, and refunds">
        <LegalP>
          Payments on JiraniApp are processed through supported payment methods, including M-Pesa.
          Prices shown at the time of booking include the service fee charged by the JiraniPro and any
          applicable platform fee.
        </LegalP>
        <LegalSubhead>Platform fee</LegalSubhead>
        <LegalP>
          JiraniApp charges a platform fee on completed bookings to cover the cost of operating the
          Platform, including payment processing, customer support, and the Jirani Guarantee. This fee is
          disclosed before you confirm a booking.
        </LegalP>
        <LegalSubhead>Cancellations and refunds</LegalSubhead>
        <LegalList items={[
          "Cancellations made at least 24 hours before the scheduled service time are generally free of charge.",
          "Late cancellations or no-shows by a Client may incur a fee, which will be communicated at the time of booking.",
          "If a JiraniPro cancels, fails to show up, or does not deliver the agreed service, the Client may be entitled to a full or partial refund under the Jirani Guarantee, subject to review.",
          "Refunds are processed back to the original payment method, typically within 3 to 5 business days after approval.",
        ]} />
      </LegalSection>

      <LegalSection id="guarantee" num="04" title="The Jirani Guarantee">
        <LegalP>
          The Jirani Guarantee is our commitment to Clients that bookings made through the Platform are
          backed by identity verification of JiraniPros, a dispute resolution process, and damage
          protection for qualifying claims.
        </LegalP>
        <LegalList items={[
          "Disputes must be raised within 48 hours of the scheduled or completed service.",
          "Our Trust & Safety team reviews disputes and may request evidence such as photos, messages, or receipts from both parties.",
          "The Jirani Guarantee does not cover pre-existing damage, normal wear and tear, or issues not reported within the required timeframe.",
        ]} />
      </LegalSection>

      <LegalSection id="conduct" num="05" title="User conduct">
        <LegalP>
          To keep JiraniApp safe and trustworthy for everyone, you agree not to:
        </LegalP>
        <LegalList items={[
          "Use the Platform for any unlawful purpose or in violation of any applicable laws.",
          "Harass, threaten, or discriminate against any other user.",
          "Attempt to circumvent the Platform to avoid fees, such as arranging payment outside JiraniApp for a booking initiated on the Platform.",
          "Provide false information about your identity, qualifications, or the services offered.",
          "Misuse the review and rating system, including posting fake reviews or manipulating ratings.",
          "Attempt to access another user's account or any non-public areas of the Platform.",
        ]} />
        <LegalP>
          Violations of this section may result in suspension or permanent removal from the Platform, and
          in serious cases, may be reported to relevant authorities.
        </LegalP>
      </LegalSection>

      <LegalSection id="reviews" num="06" title="Reviews and content">
        <LegalP>
          After a service is completed, Clients may leave a rating and written review of the JiraniPro.
          Reviews must be honest, based on a genuine booking, and must not contain abusive, defamatory, or
          unlawful content.
        </LegalP>
        <LegalP>
          By submitting a review, photo, or other content to JiraniApp, you grant us a non-exclusive,
          worldwide, royalty-free licence to use, display, and distribute that content in connection with
          operating and promoting the Platform.
        </LegalP>
      </LegalSection>

      <LegalSection id="liability" num="07" title="Limitation of liability">
        <LegalP>
          JiraniApp provides the Platform on an "as is" and "as available" basis. While we work to verify
          professionals and facilitate safe transactions, we do not guarantee the quality, safety, or
          legality of services provided by JiraniPros.
        </LegalP>
        <LegalP>
          To the fullest extent permitted by law, JiraniApp's liability arising from your use of the
          Platform is limited to the amount of fees paid by you to JiraniApp in connection with the
          booking giving rise to the claim. JiraniApp is not liable for indirect, incidental, or
          consequential damages.
        </LegalP>
      </LegalSection>

      <LegalSection id="termination" num="08" title="Suspension and termination">
        <LegalP>
          You may stop using JiraniApp at any time. We may suspend or terminate your access to the
          Platform, with or without notice, if we believe you have violated these Terms, engaged in
          fraudulent activity, or pose a risk to other users or to JiraniApp.
        </LegalP>
      </LegalSection>

      <LegalSection id="changes" num="09" title="Changes to these Terms">
        <LegalP>
          We may update these Terms from time to time to reflect changes in our services, legal
          requirements, or business practices. We will notify users of material changes through the
          Platform or via SMS or email where appropriate. Continued use of JiraniApp after changes take
          effect constitutes acceptance of the updated Terms.
        </LegalP>
      </LegalSection>

      <LegalSection id="law" num="10" title="Governing law">
        <LegalP>
          These Terms are governed by the laws of the Republic of Kenya. Any disputes arising from these
          Terms or your use of JiraniApp that cannot be resolved through our dispute process will be
          subject to the exclusive jurisdiction of the courts of Kenya.
        </LegalP>
      </LegalSection>

      <LegalSection id="contact" num="11" title="Contact us">
        <LegalP>
          If you have questions about these Terms, please reach out to our team at{" "}
          <a className="jrn-legal-link" href="mailto:legal@jiraniapp.com">legal@jiraniapp.com</a> or
          through the <a className="jrn-legal-link" href="/help">Help &amp; Support</a> page.
        </LegalP>
      </LegalSection>
    </LegalPageShell>
  );
}